/**
 * The "Rate my team" tab on /rate: describe the league, fill the roster ladder,
 * get the report.
 *
 * Exists because the league tab needs a Sleeper league. Yahoo and ESPN rooms,
 * and anyone who just wants their own squad looked at, have no path through it.
 *
 * Fully client-side: the grade, the projection and every percentile are
 * computed here from committed artifacts, with no API call behind any of them.
 *
 * It READS the user's saved leagues from storage, because those already carry
 * budget, teams, position counts and scoring in house shape, so picking one
 * skips the whole form with zero mapping. Since round 4 it also WRITES: the
 * roster being entered is remembered per league (rateTeamStore.js) so a reload
 * does not throw away five minutes of typing. Nothing leaves the browser except
 * an email address the user types into the signup form themselves.
 *
 * Three steps, because a roster ladder cannot be drawn before the league's
 * slots are known: settings, ladder, report.
 *
 * SETTINGS STEP LAYOUT, and the order is the argument. A saved league answers
 * every question the manual form asks and answers them right, so the chip
 * picker is the FIRST thing on the step and selecting one goes straight to the
 * roster; the form below it is the fallback for a room the builder has never
 * seen. Its character is borrowed from the setup surfaces this app already has
 * (MockSetup, LiveBoardSetup): uppercase section overlines, one grouped block
 * per question, and the roster total shown beside the slots so the shape can be
 * checked before any typing. Theme tokens only, nothing invented.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  PASS_TD_KINDS,
  RECEPTION_KINDS,
  REFERENCE_LENS_KEY,
  REFERENCE_LENS_LABEL,
  lensFor,
  lensKeyOf,
  lensLabel,
  splitLensKey,
} from "../../data/rateLenses";
import { DEFAULT_SCORING, PLATFORM_PRESETS } from "../../data/scoring";
import { isLocalHost } from "../../utils/analyticsGate";
import { theme } from "../../data/theme";
import { fillLadderFromRead } from "../../utils/ladderFill";
import { laddersFromPaste, settingsForPaste, shapeFromPaste } from "../../utils/pasteDraft";
import { autoSubmitRoom, roomContentId } from "../../utils/autoSubmitRoom";
import { roomLabel, roomRecord, saveRoom } from "../../utils/rateSavedRooms";
import { rateTeam, roomGradesBySurplus } from "../../utils/rateTeam";
import { teamSpendBreakdown } from "../../utils/leagueSpend";
import { buildArchetype } from "../../utils/buildArchetype";
import { isQbPremiumFormat, leagueFormatLabel } from "../../utils/leagueFormat";
import {
  captureRateManualCompleted,
  captureRatePasteSubmitted,
  captureRateSavedLeagueOpened,
} from "../../utils/rateAnalytics.js";
import {
  DEFAULT_TEAM_NAME,
  clearEntry,
  isGateBypassed,
  isRateUnlocked,
  markRateUnlocked,
  nthTeamName,
  clearPaste,
  readActiveSlot,
  readPaste,
  readTeams,
  writePaste,
  writeActiveSlot,
  writeTeams,
} from "../../utils/rateTeamStore";
import { C, SZ, card, label, primaryBtn } from "../Submit/submitStyles";
import LeagueChip from "../LeagueChip";
import LocalRoomReport from "./LocalRoomReport.jsx";
import TeamEntryLadder, { entriesFromLadder, ladderSlots } from "./TeamEntryLadder.jsx";
import DraftPasteBox from "./DraftPasteBox.jsx";
import ScreenshotDrop from "./ScreenshotDrop.jsx";
import EmailAsk from "./EmailAsk.jsx";
import { fmtCount } from "./CorpusMeter.jsx";
import LineupChips from "./LineupChips.jsx";
import TeamReportView, { DataFootnote, groupTitleColor } from "./TeamReportView.jsx";
import { AddTeamCard, RoomSoFar, TeamSwitcher, teamRow } from "./TeamRoomBar.jsx";

const SAVED_LEAGUES_KEY = "auctionArchitect_savedLeagues";

/**
 * The steppers the manual form offers, with their bounds.
 *
 * 🔴 DEF AND K ARE ROWS NOW (Aug 30 2026, round 5). They were left off to keep
 * a phone form short, on the argument that neither affected anything this page
 * produces. That argument died with kdProjections2026.js: a kicker and a
 * defense are projected, they count in the headline week 1 points, and they
 * count in the slot total the similar-league bucket is cut on. A form that
 * cannot describe a roster somebody actually plays is the wrong trade, so the
 * two steppers are here and the copy apologising for their absence is gone.
 *
 * Both default to 1, the common shape, and 0 stays legal because plenty of
 * rooms start neither. What is unchanged: they never touch the market grade,
 * which is priced, and a kicker or defense price is never needed for one.
 *
 * The order is the order buildRosterSlots draws the ladder in, so the form
 * reads top to bottom exactly as the roster the next step asks for does.
 */
export const POSITION_FIELDS = [
  { key: "QB", label: "QB", min: 0, max: 3 },
  { key: "RB", label: "RB", min: 0, max: 5 },
  { key: "WR", label: "WR", min: 0, max: 6 },
  { key: "TE", label: "TE", min: 0, max: 3 },
  { key: "FLEX", label: "FLEX", min: 0, max: 3 },
  { key: "SFLEX", label: "SUPERFLEX", min: 0, max: 2 },
  { key: "DEF", label: "DEF", min: 0, max: 2 },
  { key: "K", label: "K", min: 0, max: 2 },
  { key: "BN", label: "BENCH", min: 0, max: 12 },
];

/**
 * The team counts the picker offers.
 *
 * Even only. Odd-sized auction leagues are vanishingly rare and every one in
 * the harvest corpus is even, so nine of the sixteen sizes the picker used to
 * list were choices nobody makes standing between a user and the one they do.
 */
export const TEAM_COUNTS = [8, 10, 12, 14, 16];

/**
 * 🔴 THE PICKER STOPS OFFERING ODD SIZES; IT DOES NOT SNAP AN EXISTING ONE.
 * A saved league stored with 11 teams is a real league somebody plays in, and
 * silently rounding it to 12 would change what the report was measured against
 * without saying so. So the stored value is added to the list when it is not
 * already there: it displays, it grades, and the moment the user picks anything
 * else they get an even count.
 */
export function teamOptions(current) {
  const n = Number(current);
  if (!Number.isFinite(n) || n <= 0 || TEAM_COUNTS.includes(n)) return TEAM_COUNTS;
  return [...TEAM_COUNTS, n].sort((a, b) => a - b);
}

export const MANUAL_DEFAULT = {
  name: null,
  teams: 12,
  budget: 200,
  positions: { QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, SFLEX: 0, DEF: 1, K: 1, BN: 6 },
  // The corpus mode, so the common case is one fewer thing to answer.
  lens: REFERENCE_LENS_KEY,
};

/** Saved leagues, defensively. A corrupt or absent key is simply no leagues. */
export function loadSavedLeagues() {
  try {
    const raw = JSON.parse(localStorage.getItem(SAVED_LEAGUES_KEY));
    if (!Array.isArray(raw)) return [];
    return raw.filter((l) => l && l.name && l.positions);
  } catch {
    return [];
  }
}

/**
 * Preset name -> a full house scoring object. Unknown name falls back to defaults.
 *
 * ⚠️ NO LONGER THE MANUAL FORM'S PATH. Manual entry moved to the lens grid
 * (rateLenses.js) because the three platform defaults are all half PPR and 4pt
 * pass TD, so a Standard 6pt room like Ben's could not be described at all.
 * This stays exported because it is the mapping from a PLATFORM name, which is
 * still what a saved league or an import speaks, and losing it would mean
 * re-deriving that elsewhere.
 */
export function scoringForPreset(presetName) {
  const preset = PLATFORM_PRESETS.find((p) => p.name === presetName);
  return { ...DEFAULT_SCORING, ...(preset ? preset.changes : {}) };
}

/**
 * The settings a saved league contributes. Old saved leagues predate some
 * position keys (the migration note in CLAUDE.md), so every count is read
 * through a default rather than assumed present.
 */
export function settingsFromSavedLeague(league) {
  const p = league.positions || {};
  return {
    name: league.name,
    teams: Number(league.teams) || 12,
    budget: Number(league.budget) || 200,
    positions: {
      QB: Number(p.QB) || 0,
      RB: Number(p.RB) || 0,
      WR: Number(p.WR) || 0,
      TE: Number(p.TE) || 0,
      FLEX: Number(p.FLEX) || 0,
      SFLEX: Number(p.SFLEX) || 0,
      DEF: Number(p.DEF) || 0,
      K: Number(p.K) || 0,
      BN: Number(p.BN) || 0,
    },
    scoring: league.scoring ? { ...DEFAULT_SCORING, ...league.scoring } : { ...DEFAULT_SCORING },
  };
}

/**
 * Bounded +/- control.
 *
 * NOT DraftPlan's NumberStepper: that one is built for a dense sheet (9px
 * arrows, a value/base override pair, a floor of 0 and no ceiling). A roster
 * count needs a real maximum and a thumb-sized target on a phone, so bending it
 * to fit would cost more than the twenty lines below.
 *
 * 🔴 THE POSITION WEARS ITS OWN COLOUR, THE SAME ONE THE REPORT USES. The title
 * is coloured through groupTitleColor, imported from the report rather than
 * re-derived here, so a slot the user sets up and the card that grades it are
 * the same colour by construction and cannot drift. This is the signature the
 * whole site already speaks: PlayerList, the boards and the trends charts all
 * identify a position by colouring the position TEXT.
 *
 * BENCH has no position of its own and takes POS_COLORS.BN, which is the slate
 * the roster boards already give a bench row.
 */
function CountField({ field, value, onChange }) {
  const btn = (enabled) => ({
    width: 38,
    height: 38,
    flexShrink: 0,
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1,
    borderRadius: theme.radius.lg,
    border: `1px solid ${C.border.default}`,
    background: "transparent",
    color: enabled ? C.text.primary : C.text.faint,
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.4,
  });
  const canDown = value > field.min;
  const canUp = value < field.max;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
      <span
        data-testid={`slot-title-${field.key}`}
        style={{
          flex: 1,
          fontSize: SZ.small,
          fontWeight: 800,
          letterSpacing: "0.5px",
          color: groupTitleColor(field.key),
        }}
      >
        {field.label}
      </span>
      <button
        type="button"
        aria-label={`Fewer ${field.label}`}
        disabled={!canDown}
        onClick={() => onChange(value - 1)}
        style={btn(canDown)}
      >
        −
      </button>
      <span
        data-testid={`count-${field.key}`}
        style={{ minWidth: 26, textAlign: "center", fontSize: SZ.sub, fontWeight: 800, color: C.text.primary }}
      >
        {value}
      </span>
      <button
        type="button"
        aria-label={`More ${field.label}`}
        disabled={!canUp}
        onClick={() => onChange(value + 1)}
        style={btn(canUp)}
      >
        +
      </button>
    </div>
  );
}

/**
 * One option in a scoring row. Same chip in both rows so the two read as one
 * control rather than as two unrelated widgets.
 */
function LensChip({ testId, label, active, onClick }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      data-testid={testId}
      onClick={onClick}
      style={{
        flex: "1 1 0",
        minWidth: 88,
        padding: "10px 8px",
        fontSize: SZ.small,
        fontWeight: active ? 800 : 600,
        borderRadius: theme.radius.lg,
        border: `1px solid ${active ? C.border.brand : C.border.default}`,
        background: active ? C.bg.raised : "transparent",
        color: active ? C.text.primary : C.text.secondary,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/** A labelled row of chips: the label on the left, the options filling the rest. */
function LensRow({ label: rowLabel, testId, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
      <span
        id={`${testId}-label`}
        style={{ width: 62, flexShrink: 0, fontSize: SZ.small, color: C.text.secondary }}
      >
        {rowLabel}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`${testId}-label`}
        data-testid={testId}
        style={{ flex: "1 1 200px", display: "flex", gap: 8 }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * What a returning visit opens on.
 *
 * The slot is chosen by NAME, and a stored name that no longer matches a saved
 * league falls back to the manual slot: a league can be renamed or deleted in
 * the builder between visits, and restoring that league's roster under some
 * other league's settings would grade a team nobody entered.
 *
 * The STEP is derived from the ladder rather than stored. A stored step is a
 * second thing that can disagree with the roster it describes; "has anybody
 * been entered" answers the same question from the data itself. An empty ladder
 * opens on the settings step exactly as a first visit does.
 *
 * Exported for the tests, which is also the honest way to check a restore: the
 * function that decides is the function under test.
 */
export function restoreTeamState(savedLeagues, { ignorePaste = false } = {}) {
  const blankTeams = [{ name: DEFAULT_TEAM_NAME, ladder: {} }];
  const blank = { step: "settings", paste: null, savedName: null, manual: MANUAL_DEFAULT, teams: blankTeams };
  const wanted = readActiveSlot();
  const savedName =
    wanted && (savedLeagues || []).some((l) => l.name === wanted) ? wanted : null;
  const entry = readTeams(savedName);
  if (!entry) return { ...blank, savedName };
  // 🔴 "HAS ANYBODY BEEN ENTERED" IS ASKED OF THE WHOLE ROOM. A visitor who
  // finished their own team and started a second one must not land back on the
  // settings step because the team they are on is still empty.
  const filled = entry.teams.some((t) =>
    Object.values(t.ladder).some((r) => r && r.player && r.pos)
  );
  // 🔴 A PASTED ROOM RESTORES AS A ROOM. The derivation below answers "has
  // anybody been entered", which is the right question for a typed roster and
  // the wrong one for a league that arrived whole: a filled ladder derives to
  // the entry form, so a paste came back as one team's form and the league
  // looked lost. The paste record carries the screen it was on.
  //
  // 🔴 AND IT IS ONLY THIS TAB'S RECORD TO READ WHEN THIS TAB OWNS THE ROOM.
  // A pasted league belongs to the league tab (RatePage says why), so mounted
  // as the plain team tab this component must not restore one: it would draw a
  // whole league under a heading that says "Rate my team". ignorePaste is the
  // parent saying "you are not the room right now" — the record is left
  // untouched, not cleared, because the league tab still has to find it there.
  const paste = ignorePaste ? null : readPaste();
  return {
    step: paste ? paste.step : filled ? "roster" : "settings",
    paste,
    savedName,
    // Layered over the default so a record written by an older build, or one
    // missing a key, cannot leave the form without a value to render.
    manual: savedName ? MANUAL_DEFAULT : { ...MANUAL_DEFAULT, ...(entry.settings || {}) },
    teams: entry.teams,
  };
}

export default function TeamTab({
  incomingPaste,
  incomingRoom,
  onOpenLeague,
  onPasteApplied,
  onRoomApplied,
  onRoomCleared,
  onRoomView,
  ignorePaste = false,
} = {}) {
  const savedLeagues = useMemo(() => loadSavedLeagues(), []);
  // Read ONCE, before the first paint, so a returning visit never flashes an
  // empty form and then fills itself in.
  const restored = useMemo(
    () => restoreTeamState(savedLeagues, { ignorePaste }),
    [savedLeagues, ignorePaste]
  );
  const [step, setStep] = useState(restored.step); // settings | roster | report | room
  const [manual, setManual] = useState(restored.manual);
  const [savedName, setSavedName] = useState(restored.savedName);
  // 🔴 A LEAGUE HOLDS A LIST OF TEAMS (round 6). The first is the user's own;
  // the rest are the seats they add afterwards. `active` is which one every
  // step below is looking at.
  const [teams, setTeams] = useState(restored.teams);
  const [active, setActive] = useState(restored.paste ? restored.paste.active : 0);
  // The gate is the report's to draw and the report's to open; this page only
  // needs to know whether it has been opened, because the add-another-team card
  // sits after the report and must not appear over a locked one.
  const [unlocked, setUnlocked] = useState(() => isRateUnlocked());
  // 🔴 A PASTED ROOM RETIRES THE CAMERA. Ben, Sep 1 2026: "once we fill from a
  // paste we don't need to have the screenshot still available." He is right,
  // and it is not only clutter: the screenshot reader is the lower-accuracy
  // path, and leaving it under a roster that arrived at full accuracy invites
  // somebody to upload a picture over exact prices. It comes back the moment a
  // seat is empty again, because then there IS something for it to do.
  const [fromPaste, setFromPaste] = useState(!!restored.paste);
  // Which parts of the lineup the paste PROVED, so the confirm screen can state
  // those as fact instead of asking about them. See shapeFromPaste.
  const [proven, setProven] = useState(restored.paste ? restored.paste.proven : []);
  // 🔴 THE ROOM THIS PAGE WAS HANDED BACK, BY CONTENT ID. A room reopened from
  // storage is not a new grade, so it must not fire the corpus submit again -
  // and "not firing" is the CORRECT behaviour, not merely the harmless one: the
  // server would answer duplicate:true, but a client that re-posts every league
  // a user browses is a client hammering an endpoint to be told nothing.
  //
  // An id rather than a boolean flag, because the suppression has to end the
  // moment the room actually changes. Edit a seat and come back and the content
  // is a different room with a different id, which submits exactly as a freshly
  // typed one does. A flag would silence that edit forever.
  const [restoredId, setRestoredId] = useState(null);
  // Where the ladder's Enter chain ends. The ladder owns the hop between its
  // own rows and knows nothing about this button, so it asks rather than
  // reaches: past the last row, the page decides where "next" is.
  const submitRef = useRef(null);

  // Memoised because the entries below depend on it, and a fresh {} literal
  // every render would rebuild the entries list (and the report) on every
  // keystroke anywhere on the page.
  const ladder = useMemo(() => (teams[active] && teams[active].ladder) || {}, [teams, active]);
  /** Edit the ACTIVE team's ladder. Every other team is left exactly as it was. */
  function setLadder(fn) {
    setTeams((ts) => ts.map((t, i) => (i === active ? { ...t, ladder: fn(t.ladder) } : t)));
  }

  // The settings actually in force: a chosen saved league, else the form.
  const settings = useMemo(() => {
    if (savedName) {
      const league = savedLeagues.find((l) => l.name === savedName);
      // 🔴 A SAVED LEAGUE LENDS ITS LINEUP, NOT ITS ROLL CALL. Over a pasted
      // room the saved league is the best available answer to the one question
      // the paste cannot answer - who starts - and it is NOT an authority on
      // how many teams were in this draft or what they had to spend. Those two
      // are printed on the page that was pasted. A saved league still set to
      // twelve teams would otherwise relabel a ten-team room as a partial one.
      if (league && fromPaste) {
        return { ...settingsFromSavedLeague(league), teams: manual.teams, budget: manual.budget };
      }
      if (league) return settingsFromSavedLeague(league);
    }
    return {
      // 🔴 THE LEAGUE'S NAME, WHEN THERE IS ONE, AND IT IS LOCAL. It is read off
      // a Yahoo paste when the page carries one and typed on the confirm screen
      // when it does not; it reaches the report heading and the saved room's
      // chip and it NEVER reaches the corpus payload (autoSubmitRoom rebuilds
      // that body field by field, the same way it drops the manager names).
      name: manual.name || null,
      teams: manual.teams,
      budget: manual.budget,
      positions: manual.positions,
      scoring: lensFor(manual.lens),
    };
  }, [savedName, savedLeagues, manual, fromPaste]);

  const entries = useMemo(
    () => entriesFromLadder(ladderSlots(settings.positions), ladder),
    [settings.positions, ladder]
  );
  const report = useMemo(
    () => (step === "report" ? rateTeam({ settings, entries }) : null),
    [step, settings, entries]
  );

  // Every entered team's report, for the room strip and the full room view. A
  // team with nobody in it grades as an empty report rather than being skipped,
  // so the strip's rows and the switcher's chips are the same list in the same
  // order.
  const roomEntries = useMemo(() => {
    if (step !== "report" && step !== "room") return [];
    const slots = ladderSlots(settings.positions);
    return teams.map((t) => ({ name: t.name, entries: entriesFromLadder(slots, t.ladder) }));
  }, [step, settings.positions, teams]);

  const roomReports = useMemo(
    () => roomEntries.map((t) => rateTeam({ settings, entries: t.entries })),
    [roomEntries, settings]
  );

  /**
   * Is this room FINISHED — every seat in, every seat priced?
   *
   * 🔴 ONE PREDICATE, TWO JOBS, AND THE SECOND IS WHY IT MOVED OUT HERE. It has
   * always gated the room letters (a grade is a rank inside a full, fully
   * priced room). It now also decides the report step's chrome, because "your
   * room so far" is a strip about BUILDING a room and a finished room is not
   * being built. That used to be keyed on fromPaste, which answered the wrong
   * question: a hand-entered room with every seat typed and priced is just as
   * finished as a pasted one, and it was still being shown the progress strip.
   * Origin decides nothing on this page — see RoomReport.jsx's identical-
   * presentation rule — so completeness decides it instead, once, here.
   */
  const roomComplete = useMemo(
    () =>
      settings.teams > 0 &&
      roomReports.length >= settings.teams &&
      roomReports.every((r) => r.hasPrices),
    [settings.teams, roomReports]
  );

  /**
   * A hand-built room reaching its last seat, counted once.
   *
   * 🔴 SEATS IN, NOT GRADEABLE — those are two different places to lose
   * somebody. Somebody who typed ten rosters and no dollars finished the hard
   * part and still has no grade, which is a comprehension problem; somebody who
   * stopped at seat four has a data-entry problem. `priced` keeps them apart
   * (rateAnalytics.js carries the reasoning), so the event fires on the last
   * seat either way.
   *
   * 🔴 PASTED ROOMS ARE EXCLUDED BY CONSTRUCTION, not by accident: a paste
   * arrives complete in one move and already has its own event, so counting it
   * here too would double every pasted league in the funnel.
   *
   * The ref keys on the league and its size rather than being a plain boolean,
   * so a second league typed in the same session is counted and a re-render of
   * the same one is not.
   */
  const completedRef = useRef(null);
  useEffect(() => {
    if (fromPaste) return;
    if (!(settings.teams > 0 && teams.length >= settings.teams)) return;
    const key = `${savedName || "manual"}|${settings.teams}`;
    if (completedRef.current === key) return;
    completedRef.current = key;
    captureRateManualCompleted({
      teams: settings.teams,
      format: leagueFormatLabel(settings.positions),
      priced: roomComplete,
    });
  }, [fromPaste, teams.length, settings.teams, settings.positions, savedName, roomComplete]);

  /**
   * The one list of team facts BOTH the switcher and the room strip draw from.
   *
   * 🔴 BUILT ONCE, ON PURPOSE. Two controls listing the same teams and each
   * deriving its own surplus is how they come to disagree — the same failure
   * the two league reports had before roomSource put them on one shape.
   *
   * 🔴 THE LETTER IS GATED HERE AND NOWHERE ELSE. A grade is a rank inside a
   * FULL, fully priced room; ranking the teams somebody happened to type in
   * would be the page ranking its own data-entry order. So the letters arrive
   * with the last seat, exactly as TeamRoomBar's header promises, and until then
   * every badge is the dash. roomGradesBySurplus is the same function
   * roomSource.js grades the full room report with, so a seat cannot wear one
   * letter in the switcher and another on the room page.
   *
   * roomShares is deliberately null: the mini bar draws a team's own dollars and
   * nothing else, and a "this room" median taken over a partial room would be a
   * yardstick made of whoever has been typed in so far.
   */
  const roomRows = useMemo(() => {
    if (!roomReports.length) return null;
    const grades = roomComplete ? roomGradesBySurplus(roomReports.map((r) => r.surplus)) : null;
    return roomEntries.map((t, i) => {
      const rep = roomReports[i];
      const row = teamRow(
        t.name,
        rep,
        teamSpendBreakdown({
          picks: rep.picks,
          roomShares: null,
          benchmarkFormat: rep.benchmarkFormat,
          budget: rep.budget,
          hasPrices: rep.hasPrices,
        }),
        buildArchetype({
          picks: rep.picks,
          budget: settings.budget,
          qbPremium: isQbPremiumFormat(settings.positions),
          hasPrices: rep.hasPrices,
          benchmarkFormat: rep.benchmarkFormat,
        })
      );
      // The rank travels with the letter: roomOrder.js sorts a graded room on
      // it, and it is the same rank the room report's own cards are ordered by.
      return grades ? { ...row, grade: grades[i].grade, rank: grades[i].rank } : row;
    });
  }, [roomEntries, roomReports, roomComplete, settings.budget, settings.positions]);

  const starters = ["QB", "RB", "WR", "TE", "FLEX", "SFLEX", "DEF", "K"].reduce(
    (a, k) => a + (Number(settings.positions[k]) || 0),
    0
  );
  const canContinue = starters > 0;

  // Starters plus bench: what the ladder below will actually draw, shown beside
  // the slot block so the shape can be checked before five minutes of typing.
  const rosterTotal = starters + (Number(settings.positions.BN) || 0);

  function setCount(key, n) {
    setSavedName(null);
    setManual((m) => ({ ...m, positions: { ...m.positions, [key]: n } }));
  }

  /**
   * Pick a saved league and go. The settings step has nothing left to ask once
   * a league is chosen, so stopping on it to make the user press Next again is
   * a step that exists only because the form is drawn underneath.
   *
   * The ladder comes from THAT LEAGUE'S slot, so switching between two saved
   * leagues switches between two rosters rather than carrying one into the
   * other. A league with nothing entered starts blank.
   */
  function chooseSavedLeague(name) {
    const league = savedLeagues.find((l) => l.name === name);
    // The shortcut past the settings step, counted. No properties: the only
    // things known here are the league's NAME and its shape, and the name is
    // precisely what may not travel (rateAnalytics.js carries the rule).
    captureRateSavedLeagueOpened();

    // 🔴 INSIDE A PASTED LEAGUE, PICKING A SAVED LEAGUE MEANS "USE ITS
    // SETTINGS", NOT "THROW THIS AWAY". Ben, Sep 1 2026: "I chose my league
    // from the list of my leagues, but it should just go to the league
    // ratings, not the rate my team page." It did the worst possible thing:
    // cleared the paste and dropped him on an entry form, so a click that
    // looked like "yes, this is my league" deleted the league.
    //
    // And it is the RIGHT click to make. The confirm screen exists because a
    // draft list cannot say who starts - and a saved league answers exactly
    // that, in the user's own house shape, better than any stepper they could
    // push. So the rosters are re-laid into that league's lineup and the room
    // is graded. This is the shortest correct path through the whole feature.
    if (fromPaste && league) {
      const next = settingsFromSavedLeague(league);
      setSavedName(name);
      setTeams(reshapeTeams(teams, settings.positions, next.positions));
      setStep("room");
      return;
    }

    const entry = readTeams(name);
    setFromPaste(false);
    setProven([]);
    clearPaste();
    if (onRoomCleared) onRoomCleared();
    setSavedName(name);
    setTeams(entry ? entry.teams : [{ name: DEFAULT_TEAM_NAME, ladder: {} }]);
    setActive(0);
    setStep("roster");
  }


  /**
   * A pasted draft: the whole room, in one move.
   *
   * 🔴 IT GOES INTO THE MANUAL SLOT, NEVER OVER A SAVED LEAGUE. A saved league
   * is something the user built in the builder and plays in; overwriting its
   * ten seats because somebody pasted a page would be this feature reaching
   * into work it was never given. So the paste clears the saved selection and
   * lands in the manual slot, where the settings it derives are its own to set.
   *
   * 🔴 IT LANDS ON A CONFIRM SCREEN, AND THEN ON THE WHOLE-ROOM REPORT. Ben,
   * Sep 1 2026: "When I hit fill 10 teams, it should really bring us to the
   * rate my league, not the rate my team page" - right, and an earlier cut
   * answered a league-sized question with one team's entry form.
   *
   * But it stops once on the way, because of the other thing he asked: "We
   * also really need the league settings to be able to do a really fair
   * analysis, no?" Also right. The page proves the budget, the team count, the
   * roster size, the kicker and defense slots and one-quarterback-versus-
   * superflex - and it CANNOT prove the starting lineup, which no draft list
   * anywhere records. That one unknown moves projected points and the position
   * grades. Grading through it silently would be the report stating a guess in
   * the same voice it states the prices. So the confirm screen shows what was
   * read as fact and asks the one question that is genuinely open.
   */
  function fillFromPaste(read, text) {
    const shaped = settingsForPaste(read, MANUAL_DEFAULT, text);
    /* 🔴 THE ONE JOINT BOTH PASTE ENTRANCES PASS THROUGH. The landing page's
       box hands its read here through `incomingPaste` and the team tab's box
       calls this directly, so counting it here counts both exactly once — and
       it is counted AFTER settingsForPaste, which is the first moment the
       league's format is a fact rather than a default. Seat count and format
       only; the seats have names and the names do not travel. */
    captureRatePasteSubmitted({
      ok: !!read.ok,
      teams: read.teamCount,
      format: leagueFormatLabel(shaped.positions),
    });
    setSavedName(null);
    setManual(shaped);
    setTeams(laddersFromPaste(read, shaped.positions).map((t) => ({ name: t.name, ladder: t.ladder })));
    setActive(0);
    setFromPaste(true);
    setProven(shapeFromPaste(read, MANUAL_DEFAULT).proven);
    setStep("settings");
  }


  /**
   * Another seat in the same league: same settings, a blank ladder, a name.
   *
   * Straight to the roster, because the settings step has nothing to ask - the
   * league was described once and every team in it shares that description.
   */
  function addTeam(name) {
    setFromPaste(false);
    setTeams((ts) => [...ts, { name, ladder: {} }]);
    setActive(teams.length);
    setStep("roster");
  }

  /**
   * Forget this league's entry and go back to the top.
   *
   * The league selection is deliberately KEPT: somebody starting the roster
   * again is nearly always still in the same league, and clearing that too
   * would make them pick it a second time to do the thing they asked for.
   */
  function startOver() {
    // Clearing the room also tells the page above, so a pasted league that has
    // been thrown away stops occupying the league tab.
    if (fromPaste && onRoomCleared) onRoomCleared();
    setFromPaste(false);
    setProven([]);
    clearPaste();
    // 🔴 WITH A ROOM UNDER WAY THIS THROWS AWAY ONE SEAT, NOT THE ROOM. The
    // button that clears a league's whole entry cannot also be the button that
    // clears the team you are looking at: somebody on their fifth seat pressing
    // "start over" must not lose the other four. So the copy and the behaviour
    // change together - it reads "Remove this team" once there is more than one,
    // and it means exactly that. With a single team it is unchanged, which is
    // the only case it ever had.
    if (teams.length > 1) {
      const next = teams.filter((_, i) => i !== active);
      setTeams(next);
      setActive(Math.max(0, active - 1));
      setStep("roster");
      return;
    }
    setTeams([{ name: DEFAULT_TEAM_NAME, ladder: {} }]);
    setActive(0);
    // Only the manual slot's settings are the user's to reset here. A saved
    // league's settings are not ours to touch, and blanking the manual form
    // while a league is chosen would quietly discard work in a slot they are
    // not even looking at.
    if (!savedName) setManual(MANUAL_DEFAULT);
    clearEntry(savedName);
    setStep("settings");
  }

  /**
   * Put this room down and go rate a different one.
   *
   * 🔴 IT IS ONLY SAFE BECAUSE THE ROOM IS ALREADY FILED. The save effect runs
   * on reaching this step, so by the time this button exists the league is in
   * storage and this is genuinely "put it down", not "throw it away". It is in
   * the picker on the landing page the moment the page gets there.
   *
   * Everything is cleared, unlike "Start over": that button keeps the league
   * selection because somebody re-entering a roster is nearly always still in
   * the same league, and this one exists precisely because they are not.
   *
   * The slot the room was living in is cleared too. Leaving it behind would
   * mean the next visit to the team tab derives its way back into the league
   * the user just put down, which is the bug this button is meant to fix.
   */
  function rateAnother() {
    clearEntry(savedName);
    clearPaste();
    if (onRoomCleared) onRoomCleared();
    setFromPaste(false);
    setProven([]);
    setRestoredId(null);
    setSavedName(null);
    setManual(MANUAL_DEFAULT);
    setTeams([{ name: DEFAULT_TEAM_NAME, ladder: {} }]);
    setActive(0);
    setStep("settings");
  }

  /**
   * A draft pasted on the LEAGUE tab, applied here.
   *
   * 🔴 IT RUNS ONCE PER PASTE AND CLEARS ITSELF. The parent drops the record as
   * soon as it is taken, so switching back to the league tab and returning does
   * not re-fill the room over work the user has since done to it. An effect
   * that re-applied on every render would quietly undo every correction they
   * had made, which is the worst version of this feature.
   */
  useEffect(() => {
    if (!incomingPaste) return;
    fillFromPaste(incomingPaste.read, incomingPaste.text);
    if (onPasteApplied) onPasteApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingPaste]);

  /**
   * Persist on every change, into the slot the user is actually in.
   *
   * The manual settings ride along only in the manual slot; a saved league's
   * settings stay in the saved league, so editing that league in the builder
   * reaches this page rather than being shadowed by a stale copy.
   *
   * Writes are unconditional rather than debounced. One small localStorage
   * write per keystroke is cheaper than the bug where a roster typed quickly
   * and closed immediately is the one that does not survive.
   *
   * 🔴 NOTHING TO REMEMBER MEANS NO RECORD, NOT AN EMPTY ONE. An empty record
   * restores identically to no record, so writing one would only mean "Start
   * over" left a trace of itself in storage and the guarantee that it clears
   * the entry would be technically false. A slot is worth keeping when a
   * player has been entered, or when the manual form has been moved off its
   * defaults.
   */
  useEffect(() => {
    writeActiveSlot(savedName);
    const entered = teams.some((t) =>
      Object.values(t.ladder).some((r) => r && r.player && r.pos)
    );
    const described = !savedName && JSON.stringify(manual) !== JSON.stringify(MANUAL_DEFAULT);
    // A second empty seat is worth remembering too: somebody who pressed "add
    // another team" and closed the tab should find it waiting.
    if (entered || described || teams.length > 1) {
      writeTeams(savedName, { teams, settings: savedName ? null : manual });
    } else {
      clearEntry(savedName);
    }
  }, [savedName, teams, manual]);

  // 🔴 THE PASTED ROOM REMEMBERS ITS SCREEN, and that is the whole fix for
  // "if I click away I lose the league". The rosters were always saved; the
  // VIEW was not, so returning derived its way back to one team's entry form.
  //
  // 🔴 IT IS ALSO A WRITE THIS TAB MUST NOT MAKE WHEN IT IS NOT THE ROOM. As
  // the plain team tab, fromPaste is false by construction (the record was
  // ignored on mount), so the else branch would DELETE a league the reader is
  // in the middle of rating on the other tab. Not this tab's record, not this
  // tab's write.
  useEffect(() => {
    if (ignorePaste) return;
    if (fromPaste) writePaste({ step, active, proven });
    else clearPaste();
  }, [ignorePaste, fromPaste, step, active, proven]);

  // Which named lens the grade actually used. A saved league brings its own
  // scoring in house shape and has no lens key, so it says so rather than
  // naming a preset that was not consulted. Read by the corpus submit AND by
  // the saved-room id, which must be the same string in both or one room would
  // have two identities.
  const scoringPreset = savedName ? "saved_league" : manual.lens;

  /**
   * 🔴 A ROOM WITH PRICES IN IT HAS SOMETHING TO CONTRIBUTE, so it sends.
   *
   * The corpus is a market. A room typed without dollars is a list of names and
   * carries no price signal, so it sends nothing at all; a room that graded on
   * real money does, whichever way it was entered. `every` rather than `some`
   * is the room report's own all-or-nothing rule (roomView in roomSource.js):
   * a half-priced room would contribute a set of zero-dollar seats.
   */
  const roomPriced = roomReports.length > 0 && roomReports.every((r) => r.hasPrices);

  // 🔴 A GRADED ROOM SENDS ITSELF (Sep 2 2026, both paths since Sep 3). Reaching
  // the room step IS the grade: roomReports is computed above and
  // LocalRoomReport is about to draw it. So this is the moment the league
  // exists, and it is the moment it joins the data.
  //
  // Deliberately NOT gated on the email gate. The gate decides whether this
  // reader may look at ten graded teams; it has nothing to do with whether the
  // prices they entered are worth having, and holding the corpus write behind
  // an address would mean the rooms we learn from are only the rooms whose
  // owner signed up.
  //
  // 🔴 THE HAND-BUILT PATH SENDS HERE NOW, AND ITS OLD BUTTON IS GONE. It used
  // to post through SendRoomCard on a tap, with every team under the manager
  // name the user typed; this posts seat indices and no names at all. See
  // autoSubmitRoom.js's header - the ask was guarding the looser payload of the
  // two. `source` keeps the two populations separable forever.
  //
  // autoSubmitRoom fires and forgets: nothing here awaits it, branches on it or
  // renders it.
  //
  // 🔴 AND NOT FOR A ROOM REOPENED FROM STORAGE. Restoring a league somebody
  // already graded is not a new grade, so it does not post. The comparison omits
  // `source` because the SAVED id does (rateSavedRooms builds it that way, so a
  // league keeps one identity however it arrived) - see keyOf's header.
  useEffect(() => {
    if (step !== "room" || !roomPriced) return;
    if (restoredId && roomContentId({ settings, teams: roomEntries, scoringPreset }) === restoredId)
      return;
    autoSubmitRoom({
      settings,
      teams: roomEntries,
      source: fromPaste ? "rate_paste" : "rate_manual",
      scoringPreset,
    });
  }, [fromPaste, step, roomPriced, settings, roomEntries, scoringPreset, restoredId]);

  // 🔴 A GRADED ROOM FILES ITSELF (Sep 2 2026). Reaching this step is the
  // grade, exactly as it is for the corpus submit above, so it is also the
  // moment the room becomes something worth being able to come back to. Local
  // only, names and all: rateSavedRooms.js has the argument.
  //
  // BOTH PATHS, not just the paste. A room typed in seat by seat is the most
  // expensive thing anybody does on this page, and "Rate another league" cannot
  // be offered over a room that was never saved - that button would be a delete
  // key with a friendly label.
  //
  // Re-saving on every render at this step is free and correct: saveRoom keys
  // on the content id, so an unchanged room overwrites its own row rather than
  // growing the list.
  useEffect(() => {
    if (step !== "room") return;
    const rec = roomRecord({
      settings,
      roomEntries,
      scoringPreset,
      savedName,
      manual,
      teams,
      fromPaste,
      proven,
    });
    if (rec) saveRoom(rec);
  }, [step, settings, roomEntries, scoringPreset, savedName, manual, teams, fromPaste, proven]);

  /**
   * A room reopened from the picker on the landing page.
   *
   * It restores TeamTab's OWN state rather than a resolved settings object,
   * because the settings in force are derived from a saved league or from the
   * manual form depending on which the reader used, and rebuilding them from a
   * snapshot would reopen a saved-league room as a manual one.
   *
   * Runs once per handoff and clears itself, the same contract incomingPaste
   * has: an effect that re-applied on every render would undo every correction
   * the user made after reopening.
   */
  useEffect(() => {
    if (!incomingRoom) return;
    const st = incomingRoom.state;
    setSavedName(st.savedName);
    setManual(st.manual ? { ...MANUAL_DEFAULT, ...st.manual } : MANUAL_DEFAULT);
    setTeams(st.teams);
    setActive(0);
    setFromPaste(st.fromPaste);
    setProven(st.proven);
    setRestoredId(incomingRoom.id);
    setStep("room");
    if (onRoomApplied) onRoomApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingRoom]);

  /**
   * 🔴 WHICH VIEWS ARE LEAGUE-SCALE, TOLD TO THE PAGE THAT DRAWS THE COLUMN.
   * RatePage owns the shell width — one wrapper, one place — but this component
   * owns `step`, so it is the only thing that can say whether a room is on
   * screen. Two views qualify: the room report itself, and a SEAT report that
   * carries the room switcher above it, which is the control Ben found cramped.
   * A settings form and a roster ladder are one-column work and stay narrow.
   *
   * The cleanup reports false, so walking out of the tab entirely narrows the
   * page rather than leaving it wide around a form.
   */
  const roomScale = step === "room" || (step === "report" && teams.length > 1);
  useEffect(() => {
    if (!onRoomView) return undefined;
    onRoomView(roomScale);
    return () => onRoomView(false);
  }, [onRoomView, roomScale]);

  // The stored lens key, split into the two facts the form asks for separately.
  // Recombined through lensKeyOf, so the key format lives in rateLenses alone.
  const lens = splitLensKey(manual.lens);
  function setLens(key) {
    setSavedName(null);
    setManual((m) => ({ ...m, lens: key }));
  }

  if (step === "room") {
    // 🔴 THE PASTED ROOM IS GATED, BY BEN'S CALL (Sep 1 2026). Ten graded teams
    // is the largest thing this page gives away, and it arrives after ten
    // seconds of the reader's effort rather than twenty minutes of typing - so
    // it is the one place an address is worth asking for. A hand-typed room is
    // NOT gated here: that reader passed the gate on their own team's report
    // several teams ago, and asking twice for work they already did would be
    // the page forgetting them.
    if (fromPaste && !unlocked) {
      return (
        <RoomGate
          /* Any seat's report carries the league-level format context, and
             roomReports is already computed at this step. */
          report={roomReports[0]}
          teamCount={teams.length}
          leagueName={settings.name}
          onUnlock={() => setUnlocked(true)}
          onBack={() => setStep("settings")}
        />
      );
    }
    return (
      <div>
        <GateBadge />
        {/* Back from a PASTED room goes to the roster, not to one team's
            report. The paster has not looked at a roster yet, and "back" from a
            room they arrived at directly should be the place they can fix a
            row - not a second report they never asked for. */}
        <LocalRoomReport
          teams={teams.map((t, i) => ({ name: t.name, report: roomReports[i] }))}
          settings={settings}
          leagueName={settings.name}
          /* The funnel's word for how this room got here. `room.source` is
             "local" for both, and these are the two paths worth telling apart. */
          viewSource={fromPaste ? "paste" : "manual"}
          onBack={() => setStep(fromPaste ? "settings" : "report")}
          onRateAnother={rateAnother}
          onOpenTeam={(i) => {
            setActive(i);
            setStep("report");
          }}
        />
        {/* 🔴 THE ASK IS FOR HAND-BUILT ROOMS ONLY NOW. A pasted room has
            already gone (see the auto-submit effect above), so offering to send
            it would be the page asking for something it took a minute ago. A
            room somebody typed seat by seat - including one typed after a paste
            failed to read - still sends on an explicit tap and never otherwise.
            The footnote is what a pasted room gets instead: one line saying
            where its numbers went. */}
        {/* 🔴 THE DISCLOSURE IS DRAWN WHERE IT IS TRUE, and since Sep 3 that is
            every priced room rather than only a pasted one. A room typed
            without dollars sends nothing, so it says nothing. */}
        {roomPriced && <DataFootnote />}
      </div>
    );
  }

  if (step === "report" && report) {
    return (
      <div>
        <GateBadge />
        {/* 🔴 A FINISHED SEAT KEEPS ITS WAY HOME; AN UNFINISHED ROOM KEEPS ITS
            PROGRESS. The chrome above a seat report is keyed on whether the ROOM
            is done, never on where it came from - a hand-entered room with every
            seat typed and priced is as finished as a pasted one, and it was
            being handed "your room so far", a strip whose whole content is a
            count of how much typing is left. A finished room gets the way back
            to the league plus the switcher, which already lists every seat; an
            unfinished one keeps the switcher and the strip exactly as before.
            Pasted rooms arrive complete, so their page does not move.

            A reader with one team sees none of it, and the page is exactly what
            it was before round 6: a league of one has no room to go back to. */}
        {teams.length > 1 &&
          (roomComplete ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  data-testid="back-to-league"
                  onClick={() => setStep("room")}
                  style={{
                    padding: "8px 12px",
                    fontSize: SZ.small,
                    fontWeight: 800,
                    borderRadius: theme.radius.lg,
                    border: `1px solid ${C.border.brand}`,
                    background: "transparent",
                    color: C.brand.accent,
                    cursor: "pointer",
                  }}
                >
                  ‹ Back to the league
                </button>
              </div>
              <TeamSwitcher teams={teams} active={active} onPick={setActive} rows={roomRows} />
            </>
          ) : (
            <>
              <TeamSwitcher teams={teams} active={active} onPick={setActive} rows={roomRows} />
              <RoomSoFar
                teams={teams}
                rows={roomRows}
                teamCount={settings.teams}
                onOpenRoom={() => setStep("room")}
              />
            </>
          ))}
        <TeamReportView
          report={report}
          leagueName={settings.name}
          usedSavedLeague={!!savedName}
          /* 🔴 THE FOOTER LINE IS TRUE OF EVERY PRICED ROOM (Sep 3 2026). Both
             paths send now, so the line is no longer a paste-path fact; what it
             is still gated on is PRICES, because a roster typed without dollars
             sends nothing and must not claim its numbers joined the data. */
          sharesData={roomPriced}
          /* Same word the room report records, so the gate's conversion can be
             read against the view it came from. */
          source={fromPaste ? "paste" : "manual"}
          onEdit={() => setStep("roster")}
          /* The report's own ending when the corpus cannot place this league
             size: a route, not a sentence telling somebody to find a tab. */
          onOpenLeague={onOpenLeague}
          onUnlocked={() => setUnlocked(true)}
        />

        {/* 🔴 AFTER THE REPORT AND ONLY ONCE IT IS OPEN. Offering the next seat
            over a gated report would be asking somebody to do more typing
            before they have seen what the first lot bought them. */}
        {unlocked && teams.length < settings.teams && (
          <AddTeamCard
            key={teams.length}
            defaultName={nthTeamName(teams.length)}
            onAdd={addTeam}
          />
        )}
      </div>
    );
  }

  if (step === "roster") {
    return (
      <div>
        {teams.length > 1 && (
          <TeamSwitcher
            teams={teams}
            active={active}
            onPick={(i) => {
              setActive(i);
              setStep("roster");
            }}
          />
        )}
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: SZ.body, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {settings.name || "Your league"}
              </div>
              <div style={{ fontSize: SZ.small, color: C.text.secondary }}>
                {settings.teams} teams · ${settings.budget} budget
                {teams.length > 1 ? ` · ${teams[active].name}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <button
                type="button"
                onClick={() => setStep("settings")}
                style={{
                  padding: "8px 14px",
                  fontSize: SZ.small,
                  fontWeight: 700,
                  borderRadius: theme.radius.lg,
                  border: `1px solid ${C.border.default}`,
                  background: "transparent",
                  color: C.text.secondary,
                  cursor: "pointer",
                }}
              >
                Edit league
              </button>
              {/* The way out of a remembered roster. A link rather than a
                  button: it throws work away, and it should not look like the
                  same weight of action as the two beside it. */}
              <button
                type="button"
                data-testid="start-over"
                onClick={startOver}
                style={{
                  padding: 0,
                  fontSize: SZ.small,
                  border: "none",
                  background: "transparent",
                  color: C.text.faint,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {teams.length > 1 ? "Remove this team" : "Start over"}
              </button>
            </div>
          </div>
        </div>

        <div style={card}>
          {/* Confirm the shape BEFORE five minutes of typing goes into the
              wrong one. Editing from here returns to the settings step. */}
          <LineupChips positions={settings.positions} onEdit={() => setStep("settings")} />
          {/* Above the ladder, because it is an alternative to typing it and a
              person deciding how to fill this form has not started typing yet.
              It hands back a whole ladder rather than row patches: a read
              re-places every row it owns, which is not a per-row edit. */}
          {/* Hidden once a paste has filled this seat. See the fromPaste note
              above: a picture cannot improve on a transcript, and offering one
              over exact prices only invites somebody to make them worse. An
              emptied seat brings it straight back. */}
          {!(fromPaste && Object.keys(ladder || {}).length > 0) && (
            <ScreenshotDrop
              positions={settings.positions}
              ladder={ladder}
              onLadder={(next) => setLadder(() => next)}
            />
          )}
          <TeamEntryLadder
            positions={settings.positions}
            budget={settings.budget}
            value={ladder}
            onChange={(id, patch) =>
              setLadder((v) => {
                const next = { ...v };
                if (patch === null) delete next[id];
                else next[id] = patch;
                return next;
              })
            }
            onAdvancePastLast={() => submitRef.current && submitRef.current.focus()}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            ref={submitRef}
            onClick={() => entries.length > 0 && setStep("report")}
            disabled={entries.length === 0}
            style={{ ...primaryBtn(entries.length > 0), width: "100%" }}
          >
            {entries.length === 0 ? "Add a player to continue" : "Rate my team"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 🔴 ABOVE THE SAVED LEAGUES, BECAUSE IT IS FASTER THAN ALL OF THEM. A
          saved league still leaves sixteen rows per seat to type; a pasted
          draft results page fills every seat in the room, priced, in one move.
          It is the only control here that can finish the job by itself. */}
      {/* 🔴 WHAT THE PAGE PROVED, STATED AS FACT, ABOVE WHAT IS STILL BEING
          ASKED. The screen exists because one thing is unknowable, and it earns
          the reader's ten seconds by being explicit about how little is left:
          everything below the first line was read off their own draft. A
          confirmation screen that re-asked all of it would feel like the paste
          had not worked. */}
      {fromPaste && (
        <PasteReadCard
          settings={settings}
          proven={proven}
          teams={teams}
          /* A saved league's own scoring, or the lens the chips below are set
             to. Labelled from the same module the chips read, so the receipt
             and the control can never disagree. */
          scoringLabel={savedName ? `Saved with ${savedName}` : lensLabel(manual.lens)}
        />
      )}

      {!fromPaste && <DraftPasteBox onFill={fillFromPaste} variant="team" />}

      {/* ═════════════════════════════════════════════════════════════
          🔴 THE SAVED LEAGUE IS THE FRONT DOOR, NOT A SHORTCUT UNDER THE FORM.
          A league the user already set up carries budget, teams, position
          counts and scoring in house shape, so picking one answers every
          question the form below asks and answers them RIGHT. It sits at the
          top and it goes straight to the roster: there is nothing left for the
          settings step to ask.

          LeagueChip, not a bespoke pill. It is the universal chip this site
          identifies a league with in the header, the mock setup and the context
          strips, so a league reads the same here as everywhere else.

          With no saved leagues the row is absent ENTIRELY: no empty state, no
          "you have no leagues yet". Somebody arriving from a link has never
          used the builder, and a card telling them what they do not have is
          friction with no payoff.

          🔴 ONE EXCEPTION, AND IT IS A DEV SERVER ONE. Saved leagues live in
          localStorage, which is PER ORIGIN, so leagues saved on mock1.app are
          invisible on localhost:3000 and the other way round. That is what an
          empty picker on a dev server almost always means, and it reads as a
          broken feature rather than as an empty cupboard: round 4 was tested
          on localhost, the chip row did not appear, and nothing on the page
          said why. So on localhost, and only there, the absence gets one
          muted line naming the cause. The copy is written for a real user
          with empty storage rather than for a developer, so lifting the gate
          to everybody is a one-predicate change if that turns out to be the
          better trade on production.
          ═════════════════════════════════════════════════════════════ */}
      {savedLeagues.length > 0 && (
        <div style={{ ...card, marginBottom: 12 }} data-testid="saved-league-picker">
          <div style={sectionLabel}>{fromPaste ? "Use a saved league's lineup" : "Your leagues"}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {savedLeagues.map((l) => {
              const active = savedName === l.name;
              return (
                <button
                  key={l.name}
                  type="button"
                  data-testid={`saved-league-${l.name}`}
                  onClick={() => chooseSavedLeague(l.name)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: theme.radius.lg,
                    border: `1px solid ${active ? C.border.brand : C.border.default}`,
                    background: active ? C.bg.raised : "transparent",
                    color: C.text.primary,
                    cursor: "pointer",
                  }}
                >
                  <LeagueChip name={l.name} size="lg" />
                  <span style={{ flexShrink: 0, fontSize: SZ.small, color: C.text.secondary }}>
                    {Number(l.teams) || "?"} teams · ${Number(l.budget) || "?"}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
            {fromPaste
              ? "Fastest way to answer the lineup question: pick the league this draft was for and it rates straight away. Your team count and budget still come from the page you pasted. Nothing here changes your saved league."
              : "Read only. Nothing here changes your saved league."}
          </div>
        </div>
      )}

      {savedLeagues.length === 0 && isLocalHost() && (
        <div
          data-testid="no-saved-leagues"
          style={{ marginBottom: 12, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}
        >
          No saved leagues in this browser yet; save one in the Team Builder and it appears here.
        </div>
      )}

      <div style={card}>
        <div style={sectionLabel}>
          {fromPaste
            ? "Or set the lineup yourself"
            : savedLeagues.length > 0
              ? "Or describe the league"
              : "Describe the league"}
        </div>

        {/* 🔴 THE SOLO PATH SAYS WHAT IT IS, ONCE, AT THE TOP OF THE MANUAL FLOW.
            Hand-entering one roster is a COMPLETE route, not a fallback for
            people the paste failed: a solo team grades against the real 2026
            corpus, and the room is only needed for room-relative context. The
            page never said so, so this flow read as the leftovers under the
            paste box.

            NOT RENDERED UNDER fromPaste. After a paste the reader already has a
            room and a receipt screen telling that story, and this sentence would
            be describing a decision they have already made. */}
        {!fromPaste && (
          <div
            data-testid="team-solo-lead"
            style={{ marginTop: 8, marginBottom: 16, fontSize: SZ.small, color: C.text.faint, lineHeight: 1.5 }}
          >
            Or rate just your team: set your league, fill in your roster and prices, and you get a
            grade and percentile against real 2026 teams in your format.
          </div>
        )}

        {/* 🔴 ONE SMALL FIELD, AND ONLY WHERE IT IS THE PAGE'S TO ASK. A saved
            league already has a name and this form is explicitly read-only over
            one, so the field is absent there rather than offering to rename
            somebody's league from a grading page.

            A Yahoo paste usually answers it without being asked (the heading
            carries "League Name (ID# 12345)") and then this arrives filled in,
            editable. When the page carried no name the field is empty and the
            placeholder is the label the room would otherwise be filed under, so
            the cost of skipping it is visible before it is paid.

            Optional, always: nothing downstream needs it, and a required name
            in front of a graded room would be a second gate. */}
        {!savedName && (
          <div style={{ marginBottom: 18 }}>
            <label htmlFor="league-name" style={label}>
              Name this league (optional)
            </label>
            <input
              id="league-name"
              data-testid="league-name"
              value={manual.name || ""}
              onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
              placeholder={roomLabel({ ...settings, name: null })}
              autoComplete="off"
              style={selectStyle}
            />
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="team-teams" style={label}>
              Teams
            </label>
            <select
              id="team-teams"
              value={settings.teams}
              onChange={(e) => {
                setSavedName(null);
                setManual((m) => ({ ...m, teams: Number(e.target.value) }));
              }}
              style={selectStyle}
            >
              {teamOptions(settings.teams).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="team-budget" style={label}>
              Budget
            </label>
            <input
              id="team-budget"
              value={settings.budget}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, "");
                setSavedName(null);
                setManual((m) => ({ ...m, budget: raw === "" ? 0 : Number(raw) }));
              }}
              inputMode="numeric"
              style={{ ...selectStyle, textAlign: "right" }}
            />
          </div>
        </div>

        {/* The roster block, grouped and totalled the way LiveBoardSetup groups
            its own slot fields. A person setting up a league is answering one
            question here, not seven, and the total is what they check it
            against. */}
        <div style={sectionLabel}>
          Starting slots
          <span
            data-testid="roster-total"
            style={{
              marginLeft: 6,
              letterSpacing: 0,
              textTransform: "none",
              color: C.brand.accent,
            }}
          >
            · {rosterTotal} {rosterTotal === 1 ? "player" : "players"} per team
          </span>
        </div>
        <div style={slotGroup}>
          {POSITION_FIELDS.map((f) => (
            <CountField
              key={f.key}
              field={f}
              value={Number(settings.positions[f.key]) || 0}
              onChange={(n) => setCount(f.key, n)}
            />
          ))}
        </div>

        {savedName ? (
          <>
            <div style={sectionLabel}>Scoring</div>
            <div style={{ fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}>
              Using the scoring saved with {savedName}, exactly as you set it up.
            </div>
          </>
        ) : (
          /* 🔴 TWO DECISIONS, NOT ONE SIX-WAY CHOICE. The same six lenses sit
             underneath, but a person answers "how much per catch" and "what is
             a passing touchdown worth" separately and instantly, where a grid of
             six compound labels has to be read as six sentences and compared.
             Chips rather than a select: on a phone a select hides every option
             but one behind a tap, and there are only five options in total. */
          <div data-testid="lens-menu">
            <div style={sectionLabel}>Scoring</div>
            <LensRow label="Scoring" testId="lens-scoring">
              {RECEPTION_KINDS.map((r) => (
                <LensChip
                  key={r.slug}
                  testId={`scoring-${r.slug}`}
                  label={r.word}
                  active={lens.slug === r.slug}
                  onClick={() => setLens(lensKeyOf(r.slug, lens.passTD))}
                />
              ))}
            </LensRow>
            <LensRow label="Pass TD" testId="lens-passtd">
              {PASS_TD_KINDS.map((n) => (
                <LensChip
                  key={n}
                  testId={`passtd-${n}`}
                  label={`${n} pt`}
                  active={lens.passTD === n}
                  onClick={() => setLens(lensKeyOf(lens.slug, n))}
                />
              ))}
            </LensRow>
            <div style={{ marginTop: 8, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
              Points per reception and what a passing touchdown is worth. Those two
              move a roster more than anything else a league changes. {REFERENCE_LENS_LABEL} is
              the default because it is what most real rooms run.
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          {/* 🔴 A PASTED LEAGUE GOES STRAIGHT TO THE ROOM FROM HERE. The
              rosters are already in; this screen was only ever a confirmation,
              so its button has to be the payoff and not another leg. Somebody
              who typed their way here still has a roster to fill in. */}
          <button
            type="button"
            onClick={() => canContinue && setStep(fromPaste ? "room" : "roster")}
            disabled={!canContinue}
            style={{ ...primaryBtn(canContinue), width: "100%" }}
          >
            {fromPaste ? "Looks right, rate my Auction League" : "Next: fill in your roster"}
          </button>
        </div>

      </div>
    </div>
  );
}

/**
 * "The gate is off because this device is yours."
 *
 * 🔴 A SKIPPED GATE AND A BROKEN GATE LOOK IDENTICAL, AND THAT IS THE WHOLE
 * REASON THIS EXISTS. devDials.js makes the same argument about dialled draft
 * runs: an override with no badge is an override you forget about, and then a
 * gate that stopped working ships as "working fine, I tested it". One muted
 * line, only on a device that has opted itself out.
 */
export function GateBadge() {
  if (!isGateBypassed()) return null;
  return (
    <div
      data-testid="gate-bypassed"
      style={{
        marginBottom: 10,
        padding: "6px 10px",
        borderRadius: theme.radius.lg,
        border: `1px dashed ${C.border.default}`,
        fontSize: SZ.micro,
        color: C.text.faint,
      }}
    >
      Email gate skipped — this device is marked internal. Visitors still see it.
    </div>
  );
}

/**
 * Re-lay every seat's players into a different lineup shape.
 *
 * 🔴 IT WORKS FROM THE ROSTERS, NOT FROM THE ORIGINAL PASTE. Keeping the parsed
 * paste in memory would be simpler and would evaporate on the first reload,
 * which is precisely when somebody comes back and adjusts their lineup. The
 * rosters are already saved, and reading the players back out of them is
 * lossless - so a shape change works the same on a fresh page as on the one
 * where the paste happened.
 *
 * fillLadderFromRead is doing the placing, which is the same function the paste
 * and the screenshot reader use: one set of rules for where a player goes.
 */
export function reshapeTeams(teams, fromPositions, toPositions) {
  const slots = ladderSlots(fromPositions);
  return teams.map((t) => {
    const entries = entriesFromLadder(slots, t.ladder).map((e) => ({ ...e, confident: true }));
    return { name: t.name, ladder: fillLadderFromRead(entries, toPositions, {}).ladder };
  });
}

/**
 * The email ask in front of a pasted league's room report.
 *
 * 🔴 IT ASKS FOR THE ROOM, NOT FOR A TEAM, and the copy has to say so or the
 * promise is smaller than the thing behind it. Ten graded teams with a letter
 * each, ranked against one another, is the biggest single output this page
 * produces. Naming the number is also the proof that it exists: the count comes
 * off the rooms already parsed and sitting in memory.
 *
 * 🔴 ABSENT, NOT BLURRED. Like TeamReportView's gate, this is a different
 * return rather than a hidden branch, so there is no room report in the markup
 * for somebody with devtools to uncover. What was rendered is a form.
 *
 * The way back is out, not through: somebody unwilling to give an address can
 * return to their settings and their rosters, which are still theirs. A gate
 * with no exit is a trap, and it would take the paste with it.
 */
export function RoomGate({ report, teamCount, leagueName, onUnlock, onBack }) {
  // Both read at render off the report, never stored and never typed. See the
  // note on the blurb below for why that is load-bearing rather than tidy.
  const corpusCount = report?.nRooms ?? null;
  const formatWord = report?.benchmarkFormat === "1qb" ? "single QB" : "superflex";

  return (
    <div data-testid="room-gate">
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: SZ.sub, fontWeight: 800, lineHeight: 1.3 }}>
          Unlock all {teamCount} team grades in
          {leagueName ? ` ${leagueName}` : " your league"}
        </div>
        <div style={{ marginTop: 8, fontSize: SZ.body, color: C.text.secondary, lineHeight: 1.55 }}>
          {/* 🔴 LEADS WITH THE CORPUS, and the count is READ, never typed. Ben's
              call: the reason to hand over an address is the real data behind
              the grade, so the number goes first.

              nRooms comes off the report the page already computed - the same
              value TeamReportView prints beside the benchmark label - which
              means it tracks the harvest automatically. A typed literal is the
              class of bug that went stale on Aug 31 and again on Sep 2, when
              the cells moved 64/46 -> 70/55 -> 73/56 inside two days; nothing
              would have flagged a hardcoded 64 sitting in this sentence.

              The format word is benchmarkFormat, the CELL the grade actually
              used, not the league's own label: a 2QB room grades on the
              superflex cell, so "superflex auctions" is what it was really
              measured against. Saying "2QB auctions" would name a corpus that
              does not exist.

              Guarded because the sentence has to be true or absent. With no
              report there is no count, so the claim is not made at all rather
              than made with a blank in it. */}
          {corpusCount !== null && (
            <>
              Every seat graded against {fmtCount(corpusCount)} real 2026 {formatWord} auctions.{" "}
            </>
          )}
          A letter grade and market surplus for every seat, plus projected points and positional
          strength for all {teamCount} rosters.
        </div>
        <div style={{ marginTop: 12 }}>
          <EmailAsk
            /* 🔴 THIS PROP IS THE BUG THAT BROKE THE GATE (Sep 2 2026). It was
               absent, and EmailAsk.submit() reads report.teams/budget/isSflex/
               qbSlots/positions while BUILDING the request body - so it threw on
               undefined before fetch was ever called, and the catch printed
               "Could not reach the server" at a reader whose connection was
               fine. No request left the page, for anyone, ever.

               It is the same object TeamReportView hands EmailAsk, and it is
               passed rather than re-derived from `settings` on purpose: two
               places computing isSflex is how one of them ends up using the
               DEMAND predicate instead of the literal SFLEX slot test and files
               every 2QB league as superflex. The fields are league-level, so any
               seat's report carries them identically.

               Real values, not placeholders: api/email/trends-signup stores them
               so rows are format-segmentable, and a rate_room_gate row without
               them is a row nobody can split by format later. */
            report={report}
            testId="room-gate-ask"
            source="rate_room_gate"
            title="Enter your email address"
            blurb="Free, and your league opens here for good."
            cta="Unlock my league"
            doneText="Unlocked. Your league is below."
            askDate={false}
            onSubmitted={() => {
              markRateUnlocked();
              onUnlock();
            }}
          />
        </div>
      </div>
      <button
        type="button"
        data-testid="room-gate-back"
        onClick={onBack}
        style={{
          padding: "8px 12px",
          fontSize: SZ.small,
          fontWeight: 700,
          borderRadius: theme.radius.lg,
          border: `1px solid ${C.border.default}`,
          background: "transparent",
          color: C.text.secondary,
          cursor: "pointer",
        }}
      >
        ‹ Back to your league settings
      </button>
    </div>
  );
}

/**
 * "Here is what we read off your draft" — the top of the confirm screen.
 *
 * 🔴 IT SEPARATES READ FROM ASSUMED, ON PURPOSE. Everything listed here came
 * off the user's own page or was proved by the counts on it; the lineup rows
 * under it are the one thing being asked. Blurring the two - a single "check
 * these settings" panel - would make a reader either check nothing, and get a
 * silent wrong lineup, or check everything, and wonder why the paste bothered.
 */
function SummaryRow({ label, value, read }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        padding: "7px 0",
        borderBottom: `1px solid ${C.border.subtle}`,
      }}
    >
      <span style={{ fontSize: SZ.small, color: C.text.secondary }}>{label}</span>
      <span style={{ fontSize: SZ.body, fontWeight: 700, color: C.text.primary, textAlign: "right" }}>
        {value}
        {/* 🔴 READ AND ASSUMED ARE MARKED DIFFERENTLY, because they are not the
            same claim. A value we found on the pasted page is a fact about this
            league; a value we defaulted to is our guess, and the reader is the
            only one who can confirm it. Flattening the two would be the screen
            claiming to have read something it never saw. */}
        {!read && (
          <span style={{ marginLeft: 6, fontSize: SZ.micro, fontWeight: 600, color: C.text.faint }}>
            assumed
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * The paste's CONFIRMATION screen (Ben's call, Sep 2 2026).
 *
 * 🔴 IT IS NOT A FORM ANY MORE, IT IS A RECEIPT. Everything below was read off
 * the reader's own draft in one paste, so leading with empty-looking inputs
 * made a screen that had already done the work look like a screen asking for
 * it. The values come first, filled, and the form underneath becomes the way to
 * correct them rather than the way to supply them.
 *
 * 🔴 AND IT STILL HAS TO EARN ITS EXISTENCE. A confirmation nobody reads is
 * worse than no confirmation, so the two things a draft list genuinely cannot
 * tell us - who starts, and what the scoring is - stay called out under the
 * summary. That caveat is the reason this step is here at all; without it this
 * screen should just be skipped.
 *
 * PRESENTATION ONLY. Nothing here changes what was detected: same `settings`,
 * same `proven`, same teams.
 */
export function PasteReadCard({ settings, proven, teams, scoringLabel }) {
  const slots = teams[0] ? Object.keys(teams[0].ladder).length : 0;
  const rows = [
    { label: "Teams", value: `${teams.length}`, read: true },
    { label: "Budget", value: `$${settings.budget}`, read: true },
    { label: "Roster spots each", value: `${slots}`, read: true },
    {
      label: "Quarterbacks",
      value: proven.includes("QB") ? "1 QB, not superflex" : "1 QB",
      read: proven.includes("QB"),
    },
    {
      label: "Kicker",
      value: settings.positions.K === 1 ? "1 kicker" : "None",
      read: proven.includes("K"),
    },
    {
      label: "Defense",
      value: settings.positions.DEF === 1 ? "1 defense" : "None",
      read: proven.includes("DEF"),
    },
    // Never read off a draft list: a results page prints prices, not scoring
    // rules. This row is always the assumption, and it is the one this whole
    // screen exists to catch.
    { label: "Scoring", value: scoringLabel, read: false },
  ];

  return (
    <div style={{ ...card, marginBottom: 12 }} data-testid="paste-read-card">
      <div style={{ fontSize: SZ.sub, fontWeight: 800, lineHeight: 1.3 }}>
        Here is what we read from your draft
      </div>
      <div style={{ marginTop: 6, fontSize: SZ.body, color: C.text.secondary, lineHeight: 1.55 }}>
        Confirm it and we grade all {teams.length} seats.
      </div>
      <div style={{ marginTop: 12 }} data-testid="paste-read-summary">
        {rows.map((r) => (
          <SummaryRow key={r.label} label={r.label} value={r.value} read={r.read} />
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: SZ.body, color: C.text.primary, lineHeight: 1.55 }}>
        {/* The honest reason this screen exists at all. */}
        Two things a draft list cannot tell us: how many of those players actually{" "}
        <strong>start</strong>, and what a catch and a passing touchdown are worth. Both are
        below, and the scoring is the one worth a second look. Change anything that is wrong,
        then grade it.
      </div>
    </div>
  );
}

/**
 * The section overline: uppercase, letter-spaced, muted.
 *
 * Borrowed from LiveBoardSetup and MockSetup rather than invented, because
 * those two are the setup surfaces this page is meant to read as a sibling of.
 * Theme tokens only.
 */
const sectionLabel = {
  fontSize: theme.fontSize.micro,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: C.text.faint,
  fontWeight: theme.fontWeight.bold,
  marginBottom: 8,
  display: "block",
};

/** The steppers, boxed together so seven rows read as one control. */
const slotGroup = {
  border: `1px solid ${C.border.default}`,
  borderRadius: theme.radius.lg,
  background: C.bg.deep,
  padding: "6px 12px",
  marginBottom: 18,
};

const selectStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 10px",
  // 16px keeps iOS Safari from zooming the viewport on focus.
  fontSize: 16,
  borderRadius: theme.radius.lg,
  background: C.bg.deep,
  border: `1px solid ${C.border.default}`,
  color: C.text.primary,
};
